import { Controller, useFormContext } from 'react-hook-form';
import { Input, Tooltip } from '@dsi/react-eds';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import styles from '../swgw-scanning/CustomerSettingsForm.module.css';
import { useState } from 'react';

const JFrogSettingsForm: React.FC = () => {
  const { control, formState: { errors } } = useFormContext();
  const [showToken, setShowToken] = useState(false);

  return (
    <div className={styles.form}>
      <div className={styles.formRow}>
        <div className={styles.fieldWrapper}>
          <label htmlFor="repository_name">
            Repository Name <span className={styles.requiredAsterisk}> *</span>
            <ReactEDSWrapper
              component={Tooltip}
              message="A friendly name to identify this JFrog Artifactory repository in the system."
              pointer
            >
              <span>
                <i className={`icon icon-info ${styles.infoIcon}`}></i>
              </span>
            </ReactEDSWrapper>
          </label>
          <Controller
            name="repository_name"
            control={control}
            render={({ field }) => (
              <ReactEDSWrapper
                component={Input}
                id="repository_name"
                autoComplete="off"
                fullwidth
                placeholder="e.g., External Vendor Repository"
                className={errors.repository_name ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                {...field}
              />
            )}
          />
          {errors.repository_name && (
            <span className={styles.error}>
              <span className={styles.dangerIcon} aria-label="error" role="img">&#9888;</span>
              {typeof errors.repository_name.message === 'string' ? errors.repository_name.message : 'Required'}
            </span>
          )}
        </div>

        <div className={styles.fieldWrapper}>
          <label htmlFor="repository_url">
            Repository URL <span className={styles.requiredAsterisk}> *</span>
            <ReactEDSWrapper
              component={Tooltip}
              message="The full URL of the JFrog Artifactory instance (e.g., https://artifactory.example.com)."
              pointer
            >
              <span>
                <i className={`icon icon-info ${styles.infoIcon}`}></i>
              </span>
            </ReactEDSWrapper>
          </label>
          <Controller
            name="repository_url"
            control={control}
            render={({ field }) => (
              <ReactEDSWrapper
                component={Input}
                id="repository_url"
                autoComplete="off"
                fullwidth
                placeholder="https://artifactory.example.com"
                className={errors.repository_url ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                {...field}
              />
            )}
          />
          {errors.repository_url && (
            <span className={styles.error}>
              <span className={styles.dangerIcon} aria-label="error" role="img">&#9888;</span>
              {typeof errors.repository_url.message === 'string' ? errors.repository_url.message : 'Required'}
            </span>
          )}
        </div>
      </div>

      <div className={`${styles.formRow} ${styles.sectionSpacing}`}>
        <div className={styles.fieldWrapper}>
          <label htmlFor="repository_key">
            Repository Key <span className={styles.requiredAsterisk}> *</span>
            <ReactEDSWrapper
              component={Tooltip}
              message="The repository key from JFrog Artifactory that identifies the specific repository to access."
              pointer
            >
              <span>
                <i className={`icon icon-info ${styles.infoIcon}`}></i>
              </span>
            </ReactEDSWrapper>
          </label>
          <Controller
            name="repository_key"
            control={control}
            render={({ field }) => (
              <ReactEDSWrapper
                component={Input}
                id="repository_key"
                autoComplete="off"
                fullwidth
                placeholder="e.g., my-repo-key"
                className={errors.repository_key ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                {...field}
              />
            )}
          />
          {errors.repository_key && (
            <span className={styles.error}>
              <span className={styles.dangerIcon} aria-label="error" role="img">&#9888;</span>
              {typeof errors.repository_key.message === 'string' ? errors.repository_key.message : 'Required'}
            </span>
          )}
        </div>
      </div>

      <div className={`${styles.formRow} ${styles.sectionSpacing}`}>
        <div className={styles.field}>
          <label htmlFor="username">
            Username <span className={styles.requiredAsterisk}> *</span>
            <ReactEDSWrapper
              component={Tooltip}
              message="JFrog Artifactory username for authentication."
              pointer
            >
              <span>
                <i className={`icon icon-info ${styles.infoIcon}`}></i>
              </span>
            </ReactEDSWrapper>
          </label>
          <Controller
            name="username"
            control={control}
            render={({ field }) => (
              <ReactEDSWrapper
                component={Input}
                id="username"
                type="text"
                autoComplete="off"
                fullwidth
                placeholder="JFrog username"
                className={errors.username ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                {...field}
              />
            )}
          />
          {errors.username && (
            <span className={styles.error}>
              <span className={styles.dangerIcon} aria-label="error" role="img">&#9888;</span>
              {typeof errors.username.message === 'string' ? errors.username.message : 'Required'}
            </span>
          )}
        </div>

        <div className={styles.fieldPassword}>
          <label htmlFor="api_token">
            API Token <span className={styles.requiredAsterisk}> *</span>
            <ReactEDSWrapper
              component={Tooltip}
              message="API token or password for JFrog Artifactory authentication."
              pointer
            >
              <span>
                <i className={`icon icon-info ${styles.infoIcon}`}></i>
              </span>
            </ReactEDSWrapper>
          </label>
          <Controller
            name="api_token"
            control={control}
            render={({ field }) => (
              <ReactEDSWrapper
                component={Input}
                autoComplete="new-password"
                id="api_token"
                icon={showToken ? 'icon icon-invisible' : 'icon icon-eye'}
                onIconClick={() => setShowToken(!showToken)}
                type={showToken ? 'text' : 'password'}
                fullwidth
                placeholder="Enter API token"
                className={errors.api_token ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                {...field}
              />
            )}
          />
          {errors.api_token && (
            <span className={styles.error}>
              <span className={styles.dangerIcon} aria-label="error" role="img">&#9888;</span>
              {typeof errors.api_token.message === 'string' ? errors.api_token.message : 'Required'}
            </span>
          )}
        </div>
      </div>

      <div className={`${styles.formRow} ${styles.sectionSpacing}`}>
        <div className={styles.fieldWrapper}>
          <label htmlFor="tls_cert">
            TLS Certificate
            <ReactEDSWrapper
              component={Tooltip}
              message="Optional TLS certificate for secure connections to JFrog Artifactory. Paste the certificate content including BEGIN/END CERTIFICATE lines."
              pointer
            >
              <span>
                <i className={`icon icon-info ${styles.infoIcon}`}></i>
              </span>
            </ReactEDSWrapper>
          </label>
          <Controller
            name="tls_cert"
            control={control}
            render={({ field }) => (
              <textarea
                id="tls_cert"
                rows={6}
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                className={errors.tls_cert ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: '12px', width: '100%', minWidth: '600px' }}
                {...field}
              />
            )}
          />
          {errors.tls_cert && (
            <span className={styles.error}>
              <span className={styles.dangerIcon} aria-label="error" role="img">&#9888;</span>
              {typeof errors.tls_cert.message === 'string' ? errors.tls_cert.message : 'Invalid certificate format'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default JFrogSettingsForm;
